import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import carrierService from '@satellite/services/carrier-service';
import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import {
  EntityPresenterV2Mode,
  isEntityPresenterV2QueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterV2EntityMode';
import { normalizeCarrier } from './carrierPresenterV2Shared';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { CarrierCompanyDetails, CarrierUserDetails } from './carrierPresenterV2Shared';
import type { PresenterPlaceholderData } from './presenterV2Placeholder';

export interface UseCarrierFetcherV2Options {
  /**
   * Carrier user ID to fetch. Can be a ref for reactivity.
   * Optional if initialCarrierData (with id) is provided.
   */
  carrierId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial carrier data used as initialData for the query.
   */
  initialCarrierData?:
    | Ref<Maybe<CarrierUserDetails>>
    | ComputedRef<Maybe<CarrierUserDetails>>
    | CarrierUserDetails
    | null;

  /**
   * Optional carrier company ID to fetch. Can be a ref for reactivity.
   * Optional if carrier company (with id) is provided.
   */
  companyId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial carrier company data used as part of initialData for the query.
   */
  initialCompanyData?:
    | Ref<Maybe<CarrierCompanyDetails>>
    | ComputedRef<Maybe<CarrierCompanyDetails>>
    | CarrierCompanyDetails
    | null;

  /**
   * Optional incomplete carrier data shown as placeholderData while the query fetches.
   */
  placeholderCarrierData?:
    | Ref<Maybe<PresenterPlaceholderData<CarrierUserDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<CarrierUserDetails>>>
    | PresenterPlaceholderData<CarrierUserDetails>
    | null;

  /**
   * Optional incomplete company data merged with placeholderCarrierData for placeholderData.
   */
  placeholderCompanyData?:
    | Ref<Maybe<PresenterPlaceholderData<CarrierCompanyDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<CarrierCompanyDetails>>>
    | PresenterPlaceholderData<CarrierCompanyDetails>
    | null;

  /**
   * When true, the query runs without viewport gating.
   * Default: false
   */
  headless?: boolean;

  /**
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: no fetch until first matching EventHub event, refresh(), or invalidate()
   * - `static`: no fetch/subscriptions; returns staticCarrierData only
   */
  mode?: EntityPresenterV2Mode;

  /**
   * Data source for `mode: 'static'`.
   */
  staticCarrierData?:
    | Ref<Maybe<CarrierUserDetails>>
    | ComputedRef<Maybe<CarrierUserDetails>>
    | CarrierUserDetails
    | null;

  staleTime?: number;

  onUpdateReceived?: (carrier: CarrierUserDetails) => void;
}

export function useCarrierFetcherV2(options: UseCarrierFetcherV2Options) {
  const {
    carrierId,
    initialCarrierData,
    placeholderCarrierData,
    companyId,
    initialCompanyData,
    placeholderCompanyData,
    headless = false,
    mode = EntityPresenterV2Mode.Live,
    staticCarrierData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveCarrierId = computed((): string | null => {
    const explicitId = unref(carrierId);
    const fromObject = toValue(initialCarrierData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useCarrierFetcherV2] carrierId mismatch:',
          `explicit carrierId (${explicitId}) does not match carrier.id (${fromObject}). Using explicit carrierId.`
        );
      }
      return explicitId;
    }

    return fromObject;
  });

  const effectiveCompanyId = computed((): string | null => {
    const explicitId = unref(companyId);
    const fromObject = toValue(initialCompanyData)?.id ?? null;
    if (explicitId && fromObject && fromObject !== explicitId) {
      console.warn(
        '[useCarrierFetcherV2] companyId mismatch:',
        `explicit companyId (${explicitId}) does not match company.id (${fromObject}). Using explicit companyId.`
      );
    }

    return explicitId || fromObject;
  });

  const initialCarrier = computed(() => toValue(initialCarrierData) ?? undefined);
  const initialCompany = computed(() => toValue(initialCompanyData) ?? undefined);
  const placeholderCarrier = computed(() => toValue(placeholderCarrierData) ?? undefined);
  const placeholderCompany = computed(() => toValue(placeholderCompanyData) ?? undefined);
  const staticCarrier = computed(() => toValue(staticCarrierData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (toValue(initialCarrierData) != null ||
      toValue(initialCompanyData) != null ||
      toValue(placeholderCarrierData) != null ||
      toValue(placeholderCompanyData) != null)
  ) {
    console.warn(
      '[useCarrierFetcherV2] mode=static ignores initialCarrierData/initialCompanyData/placeholderCarrierData/placeholderCompanyData. Use staticCarrierData.'
    );
  }

  if (
    isLazyLiveMode(mode) &&
    (toValue(initialCarrierData) != null || toValue(initialCompanyData) != null)
  ) {
    console.warn(
      '[useCarrierFetcherV2] mode=lazy-live does not use initialCarrierData/initialCompanyData for the query cache; use placeholderCarrierData/placeholderCompanyData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));
  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const getCarrierInitialData = (): CarrierUserDetails | undefined => {
    const carrier = initialCarrier.value;
    const company = initialCompany.value;

    if (carrier == null && company == null) {
      return undefined;
    }

    const id = effectiveCarrierId.value ?? effectiveCompanyId.value ?? '';

    if (!id) {
      return undefined;
    }

    return {
      ...(carrier ?? {}),
      id,
      company: company != null ? { ...company } : (carrier?.company ?? undefined)
    } as CarrierUserDetails;
  };

  const getCarrierPlaceholderData = (): CarrierUserDetails | undefined => {
    const carrier = placeholderCarrier.value;
    const company = placeholderCompany.value;

    if (carrier == null && company == null) {
      return undefined;
    }

    const id = effectiveCarrierId.value ?? effectiveCompanyId.value ?? carrier?.id ?? '';

    return {
      ...(carrier ?? {}),
      id,
      company: company != null ? { ...company } : (carrier?.company ?? undefined)
    } as CarrierUserDetails;
  };

  const carrierPlaceholder = computed(() => getCarrierPlaceholderData());

  const carrierLookupKey = computed(() =>
    effectiveCarrierId.value || effectiveCompanyId.value
      ? queryKeys.carrierLookup({
          carrierId: effectiveCarrierId.value,
          companyId: effectiveCompanyId.value
        })
      : []
  );

  const carrierQuery = useQuery({
    queryKey: carrierLookupKey,
    queryFn: async ({ signal }) => {
      if (!effectiveCarrierId.value && !effectiveCompanyId.value) {
        return null;
      }

      const response = await carrierService.getCarrierV2(
        {
          ...(effectiveCarrierId.value ? { carrierId: effectiveCarrierId.value } : {}),
          ...(effectiveCompanyId.value ? { companyId: effectiveCompanyId.value } : {})
        },
        { requestConfig: { signal } }
      );

      return normalizeCarrier(response);
    },
    enabled: computed(() =>
      isEntityPresenterV2QueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveCarrierId.value || effectiveCompanyId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: () =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : getCarrierInitialData(),
    placeholderData: computed(() => (isStaticMode(mode) ? undefined : carrierPlaceholder.value)),
    staleTime,
    refetchOnWindowFocus: false
  });

  const data = computed((): CarrierUserDetails | null => {
    if (isStaticMode(mode)) {
      if (!staticCarrier.value) {
        console.warn('[useCarrierFetcherV2] mode=static requires staticCarrierData.');
        return null;
      }
      return staticCarrier.value;
    }
    return carrierQuery.data.value || null;
  });

  const isLoading = computed(() => (isStaticMode(mode) ? false : carrierQuery.isLoading.value));
  const isFetching = computed(() => (isStaticMode(mode) ? false : carrierQuery.isFetching.value));
  const error = computed(() => (isStaticMode(mode) ? null : carrierQuery.error.value));

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }

    wakeLazyLive();

    if (effectiveCarrierId.value || effectiveCompanyId.value) {
      queryClient.invalidateQueries(
        {
          queryKey: carrierLookupKey.value
        },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    await nextTick();
    await carrierQuery.refetch();
  };

  const deferOrInvalidate = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const handleUserUpdate = (event: { id?: string; companyId?: string }) => {
    const matchesCarrierId = Boolean(event?.id && event.id === effectiveCarrierId.value);
    const matchesCompanyId = Boolean(
      event?.companyId && event.companyId === effectiveCompanyId.value
    );

    if (!matchesCarrierId && !matchesCompanyId) {
      return;
    }

    deferOrInvalidate();
  };

  watch(
    data,
    newCarrier => {
      if (onUpdateReceived && newCarrier) {
        onUpdateReceived(newCarrier);
      }
    },
    { immediate: true }
  );

  watch(effectiveCarrierId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  watch(effectiveCompanyId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.on('update-User', handleUserUpdate);
      // TODO: (welington) do we have a (create|update|delete)-CarrierCompany event?
    }
  });

  onScopeDispose(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.off('update-User', handleUserUpdate);
    }
  });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    onIntersect: handleIntersect,
    query: carrierQuery
  };
}
